import {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';

import { FeatureType } from '@lessonPlanner/constants';

import GenerateButton from '../components/GenerateButton';
import GenericBanner from '../components/GenericBanner';
import AmericanLP from '../components/lesson-planner/AmericanLP';
import DefaultLp from '../components/lesson-planner/DefaultLp';
import LocalLp from '../components/lesson-planner/LocalLp';
import MontessoriLP from '../components/lesson-planner/MontessoriLP';
import MainContent from '../components/MainContent';
import GenerateLinksButton from '../components/molecules/GenerateLinksButton';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import formDefaultValues from '../constants/forms/lesson-plan/defaults';
import FORM_CONTENT_MAPPER_KEYS from '../constants/forms/lesson-plan/formContentMapperKeys';
import FORM_KEYS from '../constants/forms/lesson-plan/formKeys';
import { LP_TITLE } from '../constants/lessonPlan';
import { routes } from '../constants/routes';
import { SITE } from '../constants/site';
import CreditContext from '../context/credits/context';
import { useGeneratePause } from '../context/generate/provider';
import PortalContext from '../context/portal/context';
import { LessonPlanProperties, PageKey } from '../enums/pageKey';
import { Portal } from '../enums/portal.enum';
import SidebarLayout from '../layouts/SidebarLayout';
import LessonPlannerSchema from '../schemas/lesson-planner/LessonPlannerSchema';
import { lessonPlanProperties } from '../types/page';
import { calculateCreditUsage } from '../utils/creditUsage';
import { mapFormContents } from '../utils/mapFormContents';
import { EVENTS, trackEvent } from '../utils/tracking';

const defaultCreditCost = parseInt(
  process.env.NEXT_PUBLIC_DEFAULT_GENERATE_COST,
);
const defaultPremiumCreditCost = parseInt(
  process.env.NEXT_PUBLIC_DEFAULT_GENERATE_PREMIUM_COST,
);

function getDefaultValues(portal: Portal) {
  const defaultValues = formDefaultValues();
  switch (portal) {
    case Portal.PHILIPPINES:
      return defaultValues.LocalLp;
    case Portal.DEFAULT:
      return defaultValues.DefaultLP;
    case Portal.MONTESSORI:
      return defaultValues.MontessoriLP;
    case Portal.USA:
      return defaultValues.AmericanLP;
    default:
      return {};
  }
}

function getFormContentMapper(portal: Portal) {
  switch (portal) {
    case Portal.PHILIPPINES:
      return FORM_CONTENT_MAPPER_KEYS.LocalLp;
    case Portal.DEFAULT:
      return FORM_CONTENT_MAPPER_KEYS.DefaultLP;
    case Portal.MONTESSORI:
      return FORM_CONTENT_MAPPER_KEYS.MontessoriLP;
    case Portal.USA:
      return FORM_CONTENT_MAPPER_KEYS.AmericanLP;
    default:
      return [];
  }
}

function getCustomTavilyCallTrackedFields() {
  return [
    LessonPlanProperties.objective,
    LessonPlanProperties.objective2,
    LessonPlanProperties.objective3,
    LessonPlanProperties.objective4,
  ];
}

export default function Index(): JSX.Element {
  const router = useRouter();
  const { context: creditContext } = useContext(CreditContext);
  const { userCredit, isUserPremium, canGeneratePremium } = creditContext;
  const { paidCredits: userPaidCredits = 0 } = userCredit;
  const { status } = useSession();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { selectedPortal } = useContext(PortalContext);
  const isSignedIn = status === 'authenticated';

  const isLocalPortal = selectedPortal === Portal.PHILIPPINES;
  const isDefaultPortal = selectedPortal === Portal.DEFAULT;
  const isMontessoriPortal = selectedPortal === Portal.MONTESSORI;
  const isUSAPortal = selectedPortal === Portal.USA;

  const defaultValues = getDefaultValues(selectedPortal);

  const formContentMapper = getFormContentMapper(selectedPortal);

  const schema = LessonPlannerSchema(selectedPortal);

  const {
    formState: { errors },
    setValue,
    watch,
    trigger: triggerValidation,
    clearErrors,
  } = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(schema as ObjectSchema<unknown>),
    context: { isDefaultPortal },
  });

  const watchedValues = watch();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function trackTavilyCalls<T extends Record<string, any>>(
    formData: T,
    fields: (keyof T)[],
  ) {
    for (const field of fields) {
      const value = formData[field];
      if (!isEmpty(value)) {
        trackEvent(EVENTS.TAVILY_API_CALL, {
          value: String(value), // ensure it's a string
          fieldName: String(field),
        });
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
  }

  function handleclickTopup() {
    trackEvent(EVENTS.CLICK_TOPUP_LINK, {
      from: 'Lesson Plan Page',
    });
    router.push(routes.PRICING);
  }

  const { pausedButtonText, isGeneratePaused } = useGeneratePause();

  const hasErrors = Object.entries(errors).length > 0;

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;

  const buttonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.lessonPlan];

  const banner = (
    <GenericBanner
      showTutorial
      content={SITE.description}
      isPremium={isUserPremium}
      title={LP_TITLE}
      variation={watchedValues[FORM_KEYS.lessonFormat]?.toLowerCase()}
    />
  );

  const { regularCredits, premiumCredits } = calculateCreditUsage(
    false,
    userCredit,
    userPaidCredits,
    defaultCreditCost,
    defaultPremiumCreditCost,
  );

  const formContents = useMemo(() => {
    const mergedValues = mapFormContents<LessonPlanProperties>(
      defaultValues,
      watchedValues,
      formContentMapper,
    );

    const defaultGradeLevel = defaultValues[FORM_KEYS.gradeLevel];
    const defaultLessonFormat = defaultValues[FORM_KEYS.lessonFormat];

    let finalLessonFormat: string;

    // custom logic for local portal lesson format
    if (isLocalPortal) {
      const gradeLevel =
        mergedValues[LessonPlanProperties.gradeLevel] ?? defaultGradeLevel;

      finalLessonFormat =
        gradeLevel === 'College'
          ? 'lpCollege'
          : mergedValues[LessonPlanProperties.lessonFormat] ??
            defaultLessonFormat;
    } else {
      finalLessonFormat = defaultLessonFormat;
    }

    return {
      ...mergedValues,
      [LessonPlanProperties.lessonFormat]: finalLessonFormat,
      [LessonPlanProperties.feature]: FeatureType.LESSON_PLANNER,
    };
  }, [watchedValues, formContentMapper, defaultValues, isLocalPortal]);

  const customFormValidator = useCallback(async () => {
    await triggerValidation();

    trackTavilyCalls(
      formContents as lessonPlanProperties,
      getCustomTavilyCallTrackedFields(),
    );
  }, [triggerValidation, formContents]);

  return (
    <MainContent
      banner={banner}
      creditCost={premiumCredits}
      formContents={formContents as lessonPlanProperties}
      pageKey={PageKey.lessonPlan}
      setIsLoading={setIsLoading}
    >
      <>
        {isLocalPortal && (
          <LocalLp
            clearErrors={clearErrors}
            errors={errors}
            setValue={setValue}
            triggerValidation={triggerValidation}
            watchedValues={watchedValues}
          />
        )}

        {isDefaultPortal && (
          <DefaultLp
            clearErrors={clearErrors}
            errors={errors}
            setValue={setValue}
            triggerValidation={triggerValidation}
            watchedValues={watchedValues}
          />
        )}

        {isMontessoriPortal && (
          <MontessoriLP
            errors={errors}
            setValue={setValue}
            triggerValidation={triggerValidation}
            watchedValues={watchedValues}
          />
        )}

        {isUSAPortal && (
          <AmericanLP
            clearErrors={clearErrors}
            errors={errors}
            setValue={setValue}
            triggerValidation={triggerValidation}
            watchedValues={watchedValues}
          />
        )}

        <div className="col-span-full">
          <div className="flex flex-col">
            <GenerateButton
              isMixedCredits
              buttonText={buttonText}
              disabled={isGenerateButtonDisabled}
              premiumCredits={premiumCredits}
              regularCredits={regularCredits}
              onClick={customFormValidator}
            />

            {!canGeneratePremium && (
              <GenerateLinksButton
                handleclickTopup={handleclickTopup}
                isSignedIn={isSignedIn}
              />
            )}
          </div>
        </div>
      </>
    </MainContent>
  );
}

Index.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
